'use client';

/* -------------------------------------------------------------------------- */
/*                                 Dependecies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useEffect, useState } from 'react';
import { useReactiveVar } from '@apollo/client';

// UI lib components
import { Box, Stack, Stepper, Text } from '@mantine/core';

// Store
import dialogStoreVar from '@features/Dialog/store/dialogStore';

// Store
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';

// API
import GET_GROUPS from '@features/Group/api/getGroups';
import NEW_GROUP_FRAGMENT from '@features/Group/api/newGroupFragment';

// API & types
import {
  Group,
  useCreateGroupMutation,
  useAddUsersToGroupMutation,
  CreateGroupInput,
  GetOneGroupQueryVariables,
} from '@app-types/blackroom-api';
import { DialogGroupWizardProps } from './types';

// Style
import GroupWizardInformation from './Information';
import { useGroupColors } from '../hooks/useGroupColors';
import ConfirmBar from '@app/shared/ui-kit/molecules/ConfirmBar';
import { useTranslation } from 'react-i18next';
import { dialogGroupWizard } from './styles.css';
import { FONT_WEIGHT } from '@app/shared/ui-kit/fonts';
import GET_ONE_GROUP from '../api/getOneGroup';
import ResponsiveStepper from '@app/shared/ui-kit/molecules/ResponsiveStepper';
import { Center } from '@mantine/core';
import { useWorkspaceUsers } from '@app/features/User/hooks/useWorkspaceUsers';

const STEPS = ['information'];

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export default function DialogGroupWizard({
  onCreateGroup,
  refetchGroups,
  folderId = null,
}: DialogGroupWizardProps) {
  /* ********************************** HOOKS ********************************* */
  const workspace = useWorkspace();
  const dialogStore = useReactiveVar(dialogStoreVar);
  const groupColors = useGroupColors();
  const { t } = useTranslation(['Common', 'Group']);
  const [step, setStep] = useState(0);

  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const { refetch: refetchWorkspaceUsers } = useWorkspaceUsers();

  const [inheritedGroup, setInheritedGroup] = useState<string | null>(null);
  const [userEmails, setUserEmails] = useState<string[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    folderId,
  );

  useEffect(() => {
    setSelectedFolderId(folderId);
  }, [folderId]);

  const [createGroupInput, setCreateGroupInput] = useState<CreateGroupInput>({
    workspaceId: workspace.id,
    color: groupColors[0],
    name: '',
    groupWithAccessToClone: null,
  });

  const [addUsersToGroup] = useAddUsersToGroupMutation();
  const [createGroupMutation] = useCreateGroupMutation({
    update: (cache, { data }) => {
      const groups = cache.readQuery<{ groups: { nodes: Group[] } }>({
        query: GET_GROUPS,
      })?.groups?.nodes;
      if (groups) {
        cache.modify({
          fields: {
            groups: (existingGroups = { nodes: [] }) => {
              const newGroupRef = cache.writeFragment({
                data: data?.createGroup,
                fragment: NEW_GROUP_FRAGMENT,
              });
              return {
                ...existingGroups,
                nodes: [newGroupRef, ...existingGroups.nodes],
              };
            },
          },
        });
      }
    },
  });

  /* ***************************** CALLBACKS **************************** */

  const confirmStep = async () => {
    if (step === STEPS.length - 1) {
      await createGroup();
    } else {
      setStep(step + 1);
    }
  };

  const createGroup = async () => {
    setIsCreatingGroup(true);

    const { data } = await createGroupMutation({
      variables: {
        createGroupInput: {
          ...createGroupInput,
          groupWithAccessToClone: inheritedGroup,
          ...(selectedFolderId ? { folderId: selectedFolderId } : {}),
        },
      },
      refetchQueries: inheritedGroup ? ['GetResourcesForPermissions'] : [],
    });

    await refetchGroups();

    if (data) {
      const { createGroup: createdGroup } = data;

      if (userEmails.length > 0) {
        await addUsersToGroup({
          variables: {
            groupId: createdGroup.id,
            userEmails,
          },
          refetchQueries: [
            {
              query: GET_ONE_GROUP,
              variables: {
                groupId: createdGroup.id,
              } as GetOneGroupQueryVariables,
            },
          ],
        });
      }

      await refetchWorkspaceUsers();

      await onCreateGroup(data.createGroup as Group);

      dialogStore?.closeDialog?.('dialog-group-wizard');
    }

    setIsCreatingGroup(false);
  };

  /* ******************************** RENDERERS ******************************* */
  const renderCurrentStep = () => {
    switch (STEPS[step]) {
      case 'information':
        return (
          <GroupWizardInformation
            createGroupInput={createGroupInput}
            userEmails={userEmails}
            inheritedGroup={inheritedGroup}
            folderId={selectedFolderId}
            setCreateGroupInput={setCreateGroupInput}
            setUserEmails={setUserEmails}
            setInheritedGroup={setInheritedGroup}
            setFolderId={setSelectedFolderId}
          />
        );
      default:
        return null;
    }
  };

  /* ******************************** RENDERING ******************************* */
  return (
    <Stack className={dialogGroupWizard}>
      <Text fw={FONT_WEIGHT.bolder} c="blackroomBlack" fz={14}>
        {t('Group:DialogGroupWizard.title')}
      </Text>

      {STEPS.length > 1 && (
        <Center>
          <ResponsiveStepper active={step} maw={380} pt="xs" pb="xs">
            {STEPS.map((stepName, index) => (
              <Stepper.Step
                key={stepName}
                completedIcon={<Box>{index + 1}</Box>}
                label={t(`Group:DialogGroupWizard.steps.${stepName}.title`)}
              />
            ))}
          </ResponsiveStepper>
        </Center>
      )}

      {renderCurrentStep()}

      <ConfirmBar
        w="100%"
        onConfirm={confirmStep}
        onCancel={() =>
          step === 0
            ? dialogStore?.closeDialog?.('dialog-redaction')
            : setStep(step - 1)
        }
        saving={isCreatingGroup}
        disabled={createGroupInput.name === ''}
        labelProps={{
          cancel:
            step === 0 ? t('Common:prompts.cancel') : t('Common:cta.back'),
          confirm:
            step === STEPS.length - 1
              ? t('Common:prompts.confirm')
              : t('Common:cta.saveContinue'),
        }}
      />
    </Stack>
  );
}
